import { inject, injectable } from 'inversify'
import { setup } from 'xstate'
import {
  type MouseTipHelper,
  MouseTipId,
} from '@/plugins/cad/module/CADModule/auxiliaryTool/MouseTipHelper'
import { JoinEntityCommand } from '@/plugins/cad/module/CADModule/command/JoinEntityCommand'
import type { CADViewManager } from '@/plugins/cad/module/CADModule/manager/CADViewManager'
import type { HistoryManager } from '@/plugins/cad/module/CADModule/manager/HistoryManager'
import type { IEntity } from '@/plugins/cad/module/CADModule/model/entity'
import type { EntitySelector } from '@/plugins/cad/module/CADModule/selector/entitySelector'
import { TYPES } from '@/plugins/cad/module/container/types'
import type {
  KeyEventInfoHandler,
  KeyEventParams,
  MouseEventInfoHandler,
  MouseEventParams,
} from '@/plugins/cad/module/core/common/event'
import { SelectMode, type SelectorEventHandler } from '@/plugins/cad/module/core/selector/Selector'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import { KeyCodes } from '@/shared/constants'
import { EntityEditHandler } from './EntityEditHandler'

const JOIN_RESULT_DISPLAY_DURATION = 1000

const joinEntityMachine = setup({
  actions: {
    entrySelectEntity: () => {},
    exitSelectEntity: () => {},
    entryFinished: () => {},
  },
  guards: {
    isSelectedEntity: () => false,
  },
}).createMachine({
  id: 'joinEntity',
  initial: 'idle',
  states: {
    idle: {
      on: {
        NEXT: [{ target: 'finished', guard: 'isSelectedEntity' }, 'selectEntity'],
      },
    },
    selectEntity: {
      entry: { type: 'entrySelectEntity' },
      exit: { type: 'exitSelectEntity' },
      on: { NEXT: 'finished' },
    },
    finished: { entry: { type: 'entryFinished' }, type: 'final' },
  },
})

/**
 * 合并实体工具：将相邻或相连的实体合并为一条多段线。
 */
@injectable()
export class JoinEntityTool
  extends StateMachineTool<typeof joinEntityMachine>
  implements MouseEventInfoHandler, KeyEventInfoHandler, SelectorEventHandler<IEntity>
{
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.JoinEntityTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  protected machine = joinEntityMachine.provide({
    actions: {
      entrySelectEntity: () => this.entrySelectEntity(),
      exitSelectEntity: () => this.exitSelectEntity(),
      entryFinished: () => {
        this.entryFinished()
      },
    },
    guards: {
      isSelectedEntity: () => this.isSelectedEntity(),
    },
  })

  private selectedEntities: IEntity[] = []
  private timer: ReturnType<typeof setTimeout> | null = null

  protected onInitialize(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer)
      this.timer = null
    }
    this.selectedEntities = this.entitySelector.getSelected()
    this.entitySelector.config({ mode: SelectMode.add })
    this.historyManager.setLock(true)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    if (this.checkState('selectEntity')) {
      this.eject(this.entitySelector)
    }
    this.entitySelector.clearSelection()
    this.entitySelector.resetConfig()
    this.historyManager.setLock(false)
    if (!this.timer) {
      this.mouseTipHelper.removeTip(MouseTipId.Edit)
    }

    this.selectedEntities = []
  }

  /**
   * 鼠标按下：右键确认并执行合并。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button === 0) {
      // 左键
    } else if (params.event.button === 2 && this.checkState('selectEntity')) {
      // 右键
      this.send({ type: 'NEXT' })
    }
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
      return
    }
    if (params.event.keyCode === KeyCodes.ENTER && this.checkState('selectEntity')) {
      this.send({ type: 'NEXT' })
    }
  }

  /**
   * 选择变化回调：记录待合并的实体集合。
   *
   * @param entities - 当前已选中的实体
   */
  onSelectorChanged(entities: IEntity[]) {
    this.selectedEntities = entities
    this.mouseTipHelper.updateTip(MouseTipId.Edit, CadTipKey.edit.join.pendingCount, {
      count: this.selectedEntities.length,
    })
  }

  /************ 状态机状态处理 ************/
  private isSelectedEntity() {
    // 两个及以上的实体才能合并
    return this.selectedEntities.length >= 2
  }

  private entrySelectEntity() {
    this.inject(this.entitySelector)
    this.mouseTipHelper.addTip(MouseTipId.Edit, CadTipKey.edit.join.pendingCount, {
      count: this.selectedEntities.length,
    })
  }

  private exitSelectEntity() {
    this.eject(this.entitySelector)
  }

  private entryFinished() {
    this.executeOperate()
    this.exit()
  }

  /************ 图元数据处理 ************/
  private executeOperate() {
    if (this.selectedEntities.length === 0) {
      return
    }
    const { nonJoinedEntities, createdEntities } = EntityEditHandler.join(this.selectedEntities)
    const deleteEntities = this.selectedEntities.filter((e) => !nonJoinedEntities.includes(e))
    this.entitySelector.clearSelection()
    this.showResultTip(deleteEntities.length, nonJoinedEntities.length)
    this.historyManager.addCommandAndExecute(
      new JoinEntityCommand(createdEntities, deleteEntities, this.viewManager)
    )
  }

  private showResultTip(joined: number, skipped: number): void {
    if (this.timer !== null) {
      clearTimeout(this.timer)
    }
    this.mouseTipHelper.addTip(MouseTipId.Edit, CadTipKey.edit.join.result, {
      joined,
      skipped,
    })
    this.timer = setTimeout(() => {
      this.mouseTipHelper.removeTip(MouseTipId.Edit, CadTipKey.edit.join.result)
      this.timer = null
    }, JOIN_RESULT_DISPLAY_DURATION)
  }
}
